import { AuthMethodRoute } from 'constants/routes';
import { FormikValues } from 'formik';

export interface IDefaultConnectorProps {
	emailBody?: string;
	emailSubject?: string;
	textBody?: string;
}

export type IResetMethodTemplates = Record<
	keyof AuthMethodRoute,
	IDefaultConnectorProps
>;

export type IDefaultPasswordProps = Partial<
	Record<AuthMethodRoute, IDefaultConnectorProps>
>;

export type IConnectorDefaults = Partial<
	Record<AuthMethodRoute, IDefaultConnectorProps | IDefaultPasswordProps>
>;

export interface IConnectorMethods<T> {
	onSaveConnectors: (data: IConnectors) => Promise<IConnectors>;
	onSaveSettings: (data: T) => Promise<T>;
	templates?: IDefaultConnectorProps | undefined;
}

export enum ProviderPrefix {
	EMAIL = 'email',
	SMS = 'sms'
}

export enum ConnectorTypeEnum {
	EMAIL_PROVIDERS = 'emailProviders',
	SMS_PROVIDERS = 'smsProviders'
}

export enum ProviderPropTypes {
	EMAIL_PROVIDER = 'emailServiceProvider',
	TEXT_PROVIDER = 'textServiceProvider'
}

export enum ConnectorIndexEnum {
	SENDGRID = 'sendgrid',
	SMTP = 'smtp',
	TWILIO = 'twilio'
}

export enum ConnectorActions {
	CREATE = 'create',
	UPDATE = 'update',
	DELETE = 'delete'
}

export type ProviderTypesKeys = 'email' | 'sms';

export type ConnectorKeys = 'emailProviders' | 'smsProviders';

export type ConnectorProtocolKeys = 'sendgrid' | 'smtp' | 'twilio';

export enum CustomTemplateProps {
	EMAIL_SUBJECT = 'customEmailSubject',
	EMAIL_BODY = 'customEmailBody',
	TEXT_BODY = 'customTextMessageBody'
}

export enum TestMessagePropTypes {
	SERVICE = 'MessageProperties',
	ADHOC = 'AdHocProviderSettings'
}

export enum TestMessageTargetTypes {
	TO_EMAIL = 'EmailTo',
	TO_PHONE = 'PhoneNumberTo'
}

export const EmailConnectorTypes = [
	ConnectorIndexEnum.SENDGRID,
	ConnectorIndexEnum.SMTP
];

export const SmsConnectorTypes = [ConnectorIndexEnum.TWILIO];

export interface IConnectorSyntheticProps {
	providerProtocol: ConnectorProtocolKeys;
	providerType: ConnectorKeys;
	providerDisplayName: string;
}

export interface ICommonObj {
	common: {
		id: string;
		displayName: string;
	};
}
export interface IEmailConnectorSendGrid
	extends ICommonObj,
		IConnectorSyntheticProps {
	apiKey: string;
	apiKeyChanged: boolean;
	fromEmail: string;
}

export interface IEmailConnectorSMTP
	extends ICommonObj,
		IConnectorSyntheticProps {
	username: string;
	password: string;
	fromEmail: string;
	authChanged: boolean;
	host: string;
	port: number;
}

export interface ITextConnectorTwilio
	extends ICommonObj,
		IConnectorSyntheticProps {
	apiKey: string;
	apiSecret: string;
	apiSecretChanged: boolean;
	accountSid: string;
	fromPhone: string;
	messagingServiceSid: string;
	selectedProp: string;
	selectedAuthProp: string;
}

export type CustomAuthConnector =
	| IEmailConnectorSendGrid
	| IEmailConnectorSMTP
	| ITextConnectorTwilio;

export type CustomTemplateKeys =
	| 'emailBody'
	| 'emailSubject'
	| 'textMessageBody';

export interface IEmailCustomTemplate {
	emailBody: string;
	emailSubject: string;
}

export interface ISmsCustomTemplate {
	textMessageBody: string;
}

export type ICustomTplsInner =
	| Record<CustomTemplateKeys, IEmailCustomTemplate>
	| Record<CustomTemplateKeys, ISmsCustomTemplate>;

export interface ICustomTplObj {
	customTemplates: Record<AuthMethodRoute, ICustomTplsInner>;
}

export type IProviderTypes = Record<
	ConnectorProtocolKeys,
	CustomAuthConnector[]
>;

export interface IProviderCollection extends IProviderTypes, ICustomTplObj {}

export interface IEmailProviders extends ICustomTplObj {
	sendgrid?: IEmailConnectorSendGrid[];
	smtp?: IEmailConnectorSMTP[];
}

export interface ISMSProviders extends ICustomTplObj {
	twilio?: ITextConnectorTwilio[];
}

export interface IIdVersion {
	id: string;
	version: number;
}

export type IProvidersRecord = Record<ConnectorKeys, IProviderCollection>;

export interface IConnectors extends IIdVersion, IProvidersRecord {}

export interface IFormikValues extends FormikValues {
	setFieldValue: (key: string, val: string) => void;
}

export interface IConnectorEditDialog {
	formik: IFormikValues;
	gridSpacing: number;
}

export interface ICustomFormProps {
	customEmailBody?: string;
	customEmailSubject?: string;
	customTextMessageBody?: string;
	target?: string;
}

export type IExtendedForm<T> = T & ICustomFormProps;

export interface IOnConnectorSelectProps {
	keys: Record<string, string>;
	form: any;
	defaultTemplates: IDefaultConnectorProps | IResetMethodTemplates | undefined;
	emailCustomTemplates?: IEmailCustomTemplate;
	smsCustomTemplates?: ISmsCustomTemplate;
	resetMethod?: AuthMethodRoute;
}

export type IOnConnectorSelect = (
	props: IOnConnectorSelectProps
) => (field: string, id: string) => void;

export type ProviderType = 'emailProviderService' | 'textProviderService';

export interface IMessageProperties {
	apiKey?: string;
	apiSecret?: string;
	accountSid?: string;
	messagingServiceSid?: string;
	host?: string;
	port?: string;
}

export interface ITestMessageService {
	MessageType: ProviderPrefix;
	AuthenticationMethod: AuthMethodRoute;
	MessageProperties: IMessageProperties;
	Provider?: ConnectorProtocolKeys;
	EmailTo?: string;
	PhoneNumberTo?: string;
}

export interface ITestServicePayloadProps {
	isAdhoc: boolean;
	target: string;
	pageId: AuthMethodRoute;
	values: ITestEmailServiceValues | ITestSmsServiceValues | CustomAuthConnector;
	providerType: ProviderPrefix;
	providerProtocol: ConnectorProtocolKeys;
	providerId?: string;
}

export interface IErrorMessage {
	errorCode: string;
	message: string;
	errorDescription: string;
}

export interface ITestServicePayloadMethodProps
	extends ITestServicePayloadProps {
	onComplete: () => void;
	onFail: (props: IErrorMessage) => void;
}

export interface ITestAdHocLoginMessageRequest {
	MessageType: ProviderTypesKeys;
	AuthenticationMethod: AuthMethodRoute;
	AdHocProviderSettings: CustomAuthConnector;
	ProviderType: ConnectorProtocolKeys;
	Provider?: string;
	EmailTo?: string;
	PhoneNumberTo?: string;
}

export interface ITestEmailServiceValues {
	emailBody: string;
	emailSubject: string;
	customEmailBody: string;
	customEmailSubject: string;
	emailServiceProvider: string;
}

export interface ITestSmsServiceValues {
	textMessageBody: string;
	customTextMessageBody: string;
	textServiceProvider: string;
}

/**
 * Consts
 */
export const DEFAULT_CONNECTOR_ID = 'Descope';

export const CUSTOM_TEMPLATES_PROP = 'customTemplates';

export const MAX_PORT_VALUE = 65353;

export const DEFAULT_SMTP_PORT = 25;

export const SyntheticConnectorProps = [
	'providerType',
	'providerProtocol',
	'providerDisplayName'
];

export const emailProps = {
	emailSubject: '',
	emailBody: ''
};
export const smsProps = {
	textMessageBody: ''
};

export const InitialCustomTemplates = {
	emailProviders: {
		[AuthMethodRoute.EnchantedLink]: emailProps,
		[AuthMethodRoute.MagicLink]: emailProps,
		[AuthMethodRoute.OTP]: emailProps
	},
	smsProviders: {
		[AuthMethodRoute.MagicLink]: smsProps,
		[AuthMethodRoute.OTP]: smsProps
	}
};

export const EMAIL_CONNECTOR_SELECTOR_ID = 'email-connector-selector';
export const SMS_CONNECTOR_SELECTOR_ID = 'sms-connector-selector';

export const emailTypes = [
	ConnectorIndexEnum.SENDGRID,
	ConnectorIndexEnum.SMTP
];

export const CONNECTORS_FORM_KEYS = {
	emailSubject: 'emailSubject',
	emailBody: 'emailBody',
	textMessageBody: 'textMessageBody',
	fromPhone: 'fromPhone',
	fromEmail: 'fromEmail',
	accountSid: 'accountSid',
	host: 'host',
	username: 'username',
	password: 'password',
	serviceName: 'providerDisplayName',
	displayName: 'displayName',
	apiKey: 'apiKey',
	authToken: 'authToken',
	apiSecret: 'apiSecret',
	port: 'port',
	serverAddress: 'serverAddress',
	messagingServiceSid: 'messagingServiceSid',
	selectedProp: 'selectedProp',
	selectedAuthProp: 'selectedAuthProp',
	target: 'target',
	methodAuthToken: 'methodAuthToken',
	methodApiSecret: 'methodApiSecret'
};

export const ConnectorModelData: Partial<Record<ConnectorIndexEnum, any>> = {
	[ConnectorIndexEnum.SMTP]: {
		fromEmail: '',
		username: '',
		password: '',
		host: '',
		port: DEFAULT_SMTP_PORT,
		providerDisplayName: '',
		providerType: ConnectorTypeEnum.EMAIL_PROVIDERS,
		providerProtocol: ConnectorIndexEnum.SMTP
	},
	[ConnectorIndexEnum.SENDGRID]: {
		apiKey: '',
		apiKeyChanged: false,
		fromEmail: '',
		providerDisplayName: '',
		providerType: ConnectorTypeEnum.EMAIL_PROVIDERS,
		providerProtocol: ConnectorIndexEnum.SENDGRID
	},
	[ConnectorIndexEnum.TWILIO]: {
		apiKey: '',
		apiSecret: '',
		apiSecretChanged: false,
		accountSid: '',
		messagingServiceSid: '',
		providerDisplayName: '',
		selectedProp: CONNECTORS_FORM_KEYS.fromPhone,
		selectedAuthProp: CONNECTORS_FORM_KEYS.methodAuthToken,
		authToken: '',
		providerType: ConnectorTypeEnum.SMS_PROVIDERS,
		providerProtocol: ConnectorIndexEnum.TWILIO
	}
};

export const CUSTOM_TEMPLATE_PROPS = [
	CustomTemplateProps.EMAIL_SUBJECT,
	CustomTemplateProps.EMAIL_BODY,
	CustomTemplateProps.TEXT_BODY,
	CONNECTORS_FORM_KEYS.serviceName
];

export const EMAIL_TEST_PROPS = [
	CONNECTORS_FORM_KEYS.emailBody,
	CONNECTORS_FORM_KEYS.emailSubject,
	ProviderPropTypes.EMAIL_PROVIDER,
	CustomTemplateProps.EMAIL_SUBJECT,
	CustomTemplateProps.EMAIL_BODY
];

export const SMS_TEST_PROPS = [
	CONNECTORS_FORM_KEYS.textMessageBody,
	ProviderPropTypes.TEXT_PROVIDER,
	CustomTemplateProps.TEXT_BODY
];
